import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import routes from "./routes/pages";

import { initData, useSignal, viewport } from "@telegram-apps/sdk-react";

import { useConnectAccount, useGetProfile } from "@/pages/profile/api";
import { useEffect, useRef } from "react";
import { AUTO_OPEN_CHECKIN_KEY, parseStartAppString } from "@/shared/utils";
import SplashScreen from "@/components/common/SplashScreen.tsx";

import {
  AvailableLanguages,
  useTranslationLoader,
} from "./shared/hooks/useTranslationLoader";
import i18n from "./lib/i18n";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { InternetConnectionChecker } from "./components/internet-check-modal/InternetConnectionChecker";
import Logo from "./components/base/Logo";
import { preloadAudio } from "./lib/exposion";
import StoriesComponent from "./components/stories/StoriesModal";
import { useStoryDetail } from "./pages/home/api";
import BlockedUserSheet from "./components/common/BlockedUserSheet";

function App() {
  const { t } = useTranslation();
  const startParam = useSignal(initData.startParam);
  const location = useLocation();
  const navigate = useNavigate();
  useSearchParams();

  useEffect(() => {
    preloadAudio();
  }, []);

  useTranslationLoader(
      (localStorage.getItem("locale") as AvailableLanguages) ?? "uz"
  );

  const {
    isLoading: isLoadingProfile,
    data: profile,
  } = useGetProfile();

  const parseStartApp = parseStartAppString(startParam);

  const { data: storyData, isLoading: isStoryLoading } = useStoryDetail(
      parseStartApp?.storyId ?? ""
  );

  const { mutate: connectAccount } = useConnectAccount();

  // startapp=checkin (daily check-in reminder deep link): drop the
  // sessionStorage flag DailyCheckIn watches for, then land on home so it
  // can auto-open the check-in dialog once it mounts.
  useEffect(() => {
    if (parseStartApp?.checkin === "true") {
      sessionStorage.setItem(AUTO_OPEN_CHECKIN_KEY, "1");
      navigate("/", { replace: true });
    }
  }, [parseStartApp?.checkin, navigate]);

  useEffect(() => {
    if (profile?.id && parseStartApp?.user && parseStartApp.user !== profile.id) {
      toast.error(t("hgt_account_connected_other_account"));
    }
  }, [parseStartApp?.user, profile?.id, t]);

  useEffect(() => {
    if (parseStartApp?.con && parseStartApp?.src) {
      connectAccount({
        key: parseStartApp.con,
        source: parseStartApp.src,
      });
    }
  }, [connectAccount, parseStartApp?.con, parseStartApp?.src]);

  useEffect(() => {
    const updateViewportVars = () => {
      // viewport.* can throw or return 0 before the Telegram SDK binds the
      // viewport, and can transiently 0-out when a `viewport_changed` event
      // arrives malformed (SDK throws "Expected Object but received null").
      // Guard every read, and when the height is missing fall back to the REAL
      // measured visible height instead of CSS 100vh — on Telegram mobile 100vh
      // is taller than the visible area, so pages sized to it had their bottom
      // pushed off-screen. The visual viewport height is the accurate proxy.
      let height = 0;
      let safeAreaInsetTop = 0;
      let safeAreaInsetBottom = 0;
      try {
        height = viewport.height();
      } catch {
        /* viewport not bound yet */
      }
      try {
        safeAreaInsetTop = viewport.safeAreaInsetTop();
      } catch {
        /* ignore */
      }
      try {
        safeAreaInsetBottom = viewport.safeAreaInsetBottom();
      } catch {
        /* ignore */
      }

      const effectiveHeight =
        height > 0
          ? height
          : window.visualViewport?.height || window.innerHeight || 0;

      let styleEl = document.getElementById("dynamic-vars");
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = "dynamic-vars";
        document.head.appendChild(styleEl);
      }

      styleEl.innerHTML = `
        :root, :root * {
          --safe-area-inset-top: ${
            safeAreaInsetTop > 0 ? `${safeAreaInsetTop + 48}px` : "0px"
          } !important;
          --safe-area-inset-bottom: ${
            safeAreaInsetBottom > 0 ? `${safeAreaInsetBottom}px` : "32px"
          } !important;
          --tg-viewport-height: ${
            effectiveHeight > 0 ? `${effectiveHeight}px` : "100dvh"
          } !important;
        }
      `;
    };

    updateViewportVars();
    // viewport.mount() inside init.ts runs in setTimeout(0) — re-run after a
    // short delay so values stabilise on Android Telegram WebView, where the
    // first read can return zeros.
    const timeoutId = setTimeout(updateViewportVars, 500);
    // Keyboard, orientation, drawer scale, and Telegram expand/fullscreen all
    // emit visualViewport.resize — keep CSS vars in sync with whatever the
    // platform reports.
    const onResize = () => updateViewportVars();
    window.visualViewport?.addEventListener("resize", onResize);

    return () => {
      clearTimeout(timeoutId);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (window.ym) {
      window.ym(102062261, "hit", location.pathname + location.search);
    }
  }, [location]);

  const storiesModal = useRef(null);

  const showStories = () => {
    if (storiesModal.current) {
      // @ts-expect-error i know
      storiesModal.current.openStories(0);
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const StartParam = url.searchParams.has("tgWebAppStartParam")
    if (
        storyData &&
        !isStoryLoading &&
        parseStartApp?.storyId &&
        StartParam
    ) {
      showStories();
      if (url.searchParams.has("tgWebAppStartParam")) {
        url.searchParams.delete("tgWebAppStartParam");
        window.history.replaceState({}, document.title, url.pathname + url.search);
      }
    }
  }, [isStoryLoading, parseStartApp?.storyId, storyData]);

  if (isLoadingProfile || !profile) {
    return <SplashScreen />;
  }

  // Blocked account (is_active=false): lock the whole app behind a full-screen,
  // non-dismissable sheet. No routes/queries are mounted; the backend also
  // rejects every action with 403. Unblocking clears this on the next fetch.
  if (profile.is_active === false) {
    return (
      <>
        <LanguageSync />
        <BlockedUserSheet open reason={profile.block_reason} />
      </>
    );
  }

  return (
      <>
        <LanguageSync />
        <Logo />
        {storyData && !isStoryLoading && (
        <StoriesComponent ref={storiesModal} storiesData={[storyData]} />
      )}
      <Routes>
        {routes.map((route, index) => (
          <Route key={index} path={route.path} element={route.element}>
            {route.children?.map((childRoute, childIndex) => (
              <Route
                key={childIndex}
                path={childRoute.path}
                element={childRoute.element}
                index={childRoute.index}
              />
            ))}
          </Route>
        ))}
      </Routes>
      <InternetConnectionChecker />
    </>
  );
}

function LanguageSync() {
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      localStorage.setItem("locale", lng);
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

  return null;
}

export default App;
